import { SsoGroupRuleView } from '@evidentid/tprm-portal-lib/models/sso-provisioning';

/*
 * Stops an admin saving a group-rule change that would strip their own Admin access.
 *
 * 🚨 THIS IS A SAFETY NET, NOT AN AUTHORISATION CHECK, AND IT RUNS HERE ON PURPOSE. The server
 * cannot do it: AuthData is built from gateway headers carrying the email and the resolved RP
 * roles, never the token, so no group claim reaches eid-insurance. Forwarding one would mean a new
 * header stripped from inbound requests in every environment, or it becomes a way to assert your
 * own group membership.
 *
 * Running it in the browser is safe because the check can only ever REFUSE a save. Someone who
 * bypasses it by calling the API directly gains no privilege -- they drop their own safety net and
 * land on the server's floor, `At least one group must map to Admin`, which is still enforced on
 * every save.
 *
 * It reasons from the groups as of the last login. An IdP membership change mid-session is not
 * seen until the next one.
 */

/** The claim the Action publishes on the ID token, listing every SSO group the user holds. */
export const SSO_GROUPS_CLAIM = 'https://evidentid.com/sso_groups';

/**
 * The claim the Action published before `sso_groups`, on the ACCESS token, listing only the groups
 * that matched a rule. Read as a fallback so the guard works on tenants whose Action has not been
 * released yet; it cannot see a group with no rule, which is exactly what `sso_groups` fixes.
 * Delete this once no tenant runs an Action older than 4.4.0.
 */
export const SSO_MATCHED_GROUPS_CLAIM = 'https://evidentid.com/sso_matched_groups';

// Same comparison the backend's SsoRoleResolver makes: trimmed, case-insensitive.
const normalize = (id: string): string => id.trim().toLowerCase();

const isStringList = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((entry) => typeof entry === 'string');

// One claim out of one JWT payload. Read, never verified: this is not a security boundary, and the
// API call that follows is authenticated on its own.
function claimOf(token: string | null, name: string): string[] | null {
    if (!token) {
        return null;
    }
    try {
        const payload = token.split('.')[1];
        if (!payload) {
            return null;
        }
        const json = decodeURIComponent(
            atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
                .split('')
                .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join(''),
        );
        const claim = JSON.parse(json)[name];
        return isStringList(claim) ? claim : null;
    } catch {
        return null;
    }
}

/**
 * The caller's IdP groups, preferring the full list on the ID token and falling back to the older
 * matched-groups claim on the access token.
 *
 * `null` means "unknown" and every check below then stands aside: neither claim present (a password
 * login, an ungoverned connection, or an Entra group-overage response that omits it), or tokens
 * this cannot read. Only an explicit empty list means "in no groups".
 */
export function readCallerGroups(idToken: string | null, accessToken: string | null): string[] | null {
    return claimOf(idToken, SSO_GROUPS_CLAIM) ?? claimOf(accessToken, SSO_MATCHED_GROUPS_CLAIM);
}

// The caller's groups that a rule set grants Admin, as those rules spell them.
function ownAdminRules(callerGroups: string[] | null, rules: SsoGroupRuleView[]): string[] {
    if (!callerGroups?.length) {
        return [];
    }
    const mine = new Set(callerGroups.map(normalize));
    return rules.filter((r) => r.role === 'admin' && mine.has(normalize(r.groupObjectId))).map((r) => r.groupObjectId);
}

/**
 * Whether `draft` still grants the caller Admin.
 *
 * True whenever the caller's Admin does not come from these rules in the first place -- no claim,
 * no groups, or no group of theirs mapped to Admin in `saved`. A rules change cannot take away
 * what the rules did not give, and refusing those callers would block Evident break-glass staff
 * and anyone holding a direct grant.
 */
export function keepsOwnAdmin(
    callerGroups: string[] | null,
    saved: SsoGroupRuleView[],
    draft: SsoGroupRuleView[],
): boolean {
    if (ownAdminRules(callerGroups, saved).length === 0) {
        return true;
    }
    return ownAdminRules(callerGroups, draft).length > 0;
}

/**
 * The rows whose Remove button must be disabled: the one rule the caller's Admin hangs on.
 *
 * Empty once two or more of their groups map to Admin -- removing either still leaves the other,
 * so protecting both would block a safe edit. The save-time check in [[keepsOwnAdmin]] is what
 * catches the rest, including a downgrade that leaves the row in place.
 */
export function protectedGroupIds(callerGroups: string[] | null, saved: SsoGroupRuleView[]): string[] {
    const mine = ownAdminRules(callerGroups, saved);
    return mine.length === 1 ? mine : [];
}
